import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { api, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const RES_CLS = { won: "text-emerald-400", lost: "text-red-400", refunded: "text-slate-400" };

export const StatsGrid = () => {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/me/stats")).data });
  const s = data || { games: 0, wins: 0, won: 0, net: 0 };
  const rate = s.games ? Math.round((s.wins / s.games) * 100) : 0;
  const items = [["games", s.games], ["wins", `${s.wins} (${rate}%)`], ["won", formatXAF(s.won, lang)], ["net", formatXAF(s.net, lang)]];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="stats-grid">
      {items.map(([k, v]) => (
        <div key={k} className="surface p-4">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">{t(`history.${k}`)}</p>
          <p className={`mt-1 font-mono text-lg font-semibold ${k === "net" ? (s.net >= 0 ? "text-emerald-400" : "text-red-400") : ""}`} data-testid={`stat-${k}`}>{v}</p>
        </div>
      ))}
    </div>
  );
};

export default function HistoryPage() {
  const { t, lang } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["history"], queryFn: async () => (await api.get("/games/mine/history", { params: { limit: 50 } })).data });
  return (
    <div className="space-y-5 rise" data-testid="history-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("history.title")}</h1>
      <StatsGrid />
      <section className="surface p-5">
        {isLoading ? <p className="py-8 text-center text-slate-500">{t("common.loading")}</p> : data?.items?.length ? (
          <ul className="divide-y divide-white/5">
            {data.items.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-3 py-4" data-testid={`history-row-${g.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{t("lobby.waitingFmt", { n: g.player_count, stake: formatXAF(g.stake, lang) })}</p>
                  <p className="truncate text-xs text-slate-500">{g.players.map((p) => p.pseudo).join(", ")} · {new Date(g.finished_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR")}</p>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <div>
                    <p className={`text-xs font-semibold ${RES_CLS[g.result]}`}>{t(`history.r_${g.result}`)}</p>
                    <p className={`font-mono text-sm font-semibold ${RES_CLS[g.result]}`}>{g.net > 0 ? "+" : ""}{formatXAF(g.net, lang)}</p>
                  </div>
                  {g.status === "finished" && (
                    <Link to={`/verify?game=${g.id}`} className="grid h-11 w-11 place-items-center rounded-full border border-white/10 text-amber-400" data-testid={`history-verify-${g.id}`} aria-label="verify">
                      <Fingerprint className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : <p className="py-8 text-center text-sm text-slate-500" data-testid="history-empty">{t("history.empty")}</p>}
      </section>
    </div>
  );
}
